import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation } from 'react-router-dom';
import { motion } from "framer-motion";

const RobotAvatar = ({ isSpeaking, isRecording, isListening }) => {
  const mouthVariants = {
    idle: { scaleY: 1 },
    active: {
      scaleY: [0.8, 1.8, 0.8],
      transition: { duration: 0.3, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" },
    },
    listening: {
      scaleY: [1, 1.2, 1],
      transition: { duration: 1, repeat: Infinity, ease: "easeInOut" },
    },
  };

  const isActive = isSpeaking || isRecording;
  const animateVariant = isActive ? "active" : isListening ? "listening" : "idle";

  return (
    <div className="relative w-32 h-32">
      <motion.div
        className={`absolute inset-0 rounded-full shadow-lg border-2 border-gray-600 transition-colors duration-300 ${
          isSpeaking || isRecording ? "bg-pink-400" : isListening ? "bg-blue-400" : "bg-gray-700"
        }`}
        animate={{ scale: isActive ? 1.05 : 1 }}
      />
      <div className="absolute top-8 left-8 w-6 h-6 bg-black rounded-full" />
      <div className="absolute top-8 right-8 w-6 h-6 bg-black rounded-full" />
      <motion.div
        className="absolute bottom-10 left-1/2 transform -translate-x-1/2 w-16 h-2 bg-black rounded-full"
        variants={mouthVariants}
        animate={animateVariant}
      />
    </div>
  );
};

const AMIHome = () => {
  const [question, setQuestion] = useState("");
  const [fullTranscript, setFullTranscript] = useState("");
  const [interimTranscript, setInterimTranscript] = useState("");
  const [answer, setAnswer] = useState("");
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [inVoiceMode, setInVoiceMode] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [permissionDenied, setPermissionDenied] = useState(false);

  const inputRef = useRef(null);
  const wakeRecognitionRef = useRef(null);
  const questionRecognitionRef = useRef(null);
  const timeoutsRef = useRef({});

  const qaData = [
    { q: "Hello", a: "Hi. I am Thinky. How may I help you?" },
    { q: "What is AMI", a: "AMI Stands for Artificial MSME Intelligence, a brain child of Sanjeev Kumar, Think5Whys founder." },
    { q: "What is MSME", a: "MSME stands for Micro, Small, and Medium Enterprises." },
    { q: "How does MSME help the economy", a: "MSMEs boost employment, innovation, and local manufacturing." },
    { q: "Who can register under MSME", a: "Any micro, small, or medium business involved in manufacturing or services." },
    { q: "What is Udyam Registration", a: "It's the government registration for MSMEs under the Ministry of MSME." },
    { q: "Is MSME registration free", a: "Yes, Udyam registration is completely free and paperless." },
    { q: "Can a startup also be MSME", a: "Yes, startups can register as MSME if they meet investment and turnover limits." },
    { q: "What are MSME benefits", a: "Low-interest loans, government subsidies, and delayed payment protection." },
    { q: "What is the turnover limit for MSME", a: "Micro < ₹5Cr, Small < ₹50Cr, Medium < ₹250Cr." },
    { q: "Can MSMEs export products", a: "Yes, MSMEs are key contributors to India’s exports and get export incentives." },
    { q: "How to apply for MSME loan", a: "You can apply through banks, NBFCs, or government portals like psbloansin59minutes." },
  ];

  const clearStatus = useCallback(() => {
    setStatus("");
    setError("");
  }, []);

  const stopAllRecognitions = useCallback(() => {
    if (wakeRecognitionRef.current) {
      wakeRecognitionRef.current.stop();
      wakeRecognitionRef.current = null;
    }
    if (questionRecognitionRef.current) {
      questionRecognitionRef.current.stop();
      questionRecognitionRef.current = null;
    }
    Object.values(timeoutsRef.current).forEach((id) => clearTimeout(id));
    timeoutsRef.current = {};
  }, []);

  const exitVoiceMode = useCallback(() => {
    setInVoiceMode(false);
    setIsListening(false);
    setIsRecording(false);
    setFullTranscript("");
    setInterimTranscript("");
    setQuestion("");
    stopAllRecognitions();
    clearStatus();
    setTimeout(() => inputRef.current?.focus(), 100);
  }, [stopAllRecognitions, clearStatus]);

  const handleAsk = useCallback(() => {
    try {
      clearStatus();
      const trimmedQuestion = question.trim();
      if (!trimmedQuestion) {
        setInVoiceMode(true);
        inputRef.current?.focus();
        startWakeListening();
        setStatus("Listening for 'Thinky'... Say 'Thinky' to ask a question.");
        return;
      }
      // Text mode: Process and exit any voice mode
      exitVoiceMode();
      const found = qaData.find(
        (item) => item.q.toLowerCase().trim() === trimmedQuestion.toLowerCase()
      );
      const result = found ? found.a : "Sorry! I don't have answer to the question";
      setAnswer(result);
      setQuestion(""); // Clear after text process
      speak(result);
    } catch (err) {
      console.error("Error in handleAsk:", err);
      setError("An unexpected error occurred while processing your question. Please try again.");
      exitVoiceMode();
    }
  }, [question, clearStatus, exitVoiceMode]);

  const speak = useCallback((text) => {
    try {
      if (!("speechSynthesis" in window)) {
        setError("Speech synthesis is not supported in this browser.");
        exitVoiceMode();
        return;
      }
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.pitch = 1;
      utterance.rate = 1;
      utterance.onstart = () => setIsSpeaking(true);
      utterance.onend = () => {
        setIsSpeaking(false);
        if (inVoiceMode) {
          exitVoiceMode();
        }
      };
      utterance.onerror = (event) => {
        console.error("Speech synthesis error:", event.error);
        setIsSpeaking(false);
        setError(`Speech error: ${event.error}. Please check your audio settings.`);
        exitVoiceMode();
      };
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(utterance);
    } catch (err) {
      console.error("Error in speak:", err);
      setError("Failed to initiate speech. Please try again.");
      exitVoiceMode();
    }
  }, [inVoiceMode, exitVoiceMode]);

  const startQuestionRecording = useCallback(() => {
    try {
      clearStatus();
      if (permissionDenied) {
        setError("Microphone permission denied. Please refresh and grant access.");
        exitVoiceMode();
        return;
      }
      if (!("SpeechRecognition" in window || "webkitSpeechRecognition" in window)) {
        setError("Speech recognition not supported. Please use text input.");
        exitVoiceMode();
        return;
      }

      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      stopAllRecognitions(); // Ensure clean start

      const recognition = new SpeechRecognition();
      questionRecognitionRef.current = recognition;

      recognition.continuous = true; // Changed for better interim updates
      recognition.interimResults = true;
      recognition.lang = "en-US";
      recognition.maxAlternatives = 1;

      recognition.onstart = () => {
        setIsRecording(true);
        setStatus("Listening to your question... Speak now!");
        setFullTranscript("");
        setInterimTranscript("");
        setQuestion("");
        inputRef.current?.focus();
        // 30s timeout
        timeoutsRef.current.question = setTimeout(() => {
          recognition.stop();
          setIsRecording(false);
          setStatus("Recording timed out. Processing partial input...");
          handleAsk(); // Process whatever was captured
        }, 30000);
      };

      recognition.onresult = (event) => {
        let newFull = fullTranscript;
        let newInterim = "";
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            newFull += transcript + " ";
          } else {
            newInterim += transcript;
          }
        }
        setFullTranscript(newFull.trim());
        setInterimTranscript(newInterim);
        setQuestion(newFull.trim() + (newInterim ? " " + newInterim : ""));
      };

      recognition.onend = () => {
        Object.values(timeoutsRef.current).forEach((id) => clearTimeout(id));
        setInterimTranscript(""); // Commit interim to full
        setIsRecording(false);
        setStatus("Processing your question...");
        handleAsk();
      };

      recognition.onerror = (event) => {
        Object.values(timeoutsRef.current).forEach((id) => clearTimeout(id));
        setIsRecording(false);
        console.error("Speech recognition error:", event.error);
        let errorMsg = "An error occurred during recording.";
        if (event.error === "not-allowed") {
          errorMsg = "Microphone access denied. Please grant permission and try again.";
          setPermissionDenied(true);
        } else if (event.error === "no-speech") {
          errorMsg = "No speech detected. Try speaking louder.";
        } else if (event.error === "audio-capture") {
          errorMsg = "Microphone not available. Check hardware.";
        } else if (event.error === "network") {
          errorMsg = "Network error. Check connection.";
        } else {
          errorMsg = `Recognition error: ${event.error}`;
        }
        setError(errorMsg);
        // No restart - exit mode
        setTimeout(exitVoiceMode, 2000);
      };

      recognition.start();
    } catch (err) {
      console.error("Error starting question recording:", err);
      setError("Failed to start recording. Please check permissions.");
      setIsRecording(false);
      exitVoiceMode();
    }
  }, [clearStatus, handleAsk, permissionDenied, exitVoiceMode, stopAllRecognitions, fullTranscript, interimTranscript]);

  const startWakeListening = useCallback(() => {
    if (!inVoiceMode || permissionDenied) return;

    try {
      clearStatus();
      if (!("SpeechRecognition" in window || "webkitSpeechRecognition" in window)) {
        setError("Speech recognition not supported. Use text input.");
        exitVoiceMode();
        return;
      }

      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      stopAllRecognitions();

      const recognition = new SpeechRecognition();
      wakeRecognitionRef.current = recognition;

      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = "en-US";

      recognition.onstart = () => {
        setIsListening(true);
        setStatus("Listening for 'Thinky'...");
        // 10s timeout
        timeoutsRef.current.wake = setTimeout(() => {
          recognition.stop();
          setIsListening(false);
          setStatus("No wake word detected. Press 'Ask Thinky' to try again.");
          setTimeout(exitVoiceMode, 2000);
        }, 10000);
      };

      recognition.onresult = (event) => {
        clearTimeout(timeoutsRef.current.wake);
        let transcript = "";
        for (let i = event.resultIndex; i < event.results.length; i++) {
          transcript += event.results[i][0].transcript;
        }
        const lowerTranscript = transcript.toLowerCase().trim();
        if (lowerTranscript.includes("thinky")) {
          recognition.stop();
          setIsListening(false);
          setStatus("Wake word detected! Now ask your question...");
          setTimeout(startQuestionRecording, 500);
        }
      };

      recognition.onerror = (event) => {
        clearTimeout(timeoutsRef.current.wake);
        setIsListening(false);
        console.error("Wake listening error:", event.error);
        let errorMsg = "Listening interrupted.";
        if (event.error === "not-allowed") {
          errorMsg = "Microphone access denied. Please grant permission.";
          setPermissionDenied(true);
        } else if (event.error === "network") {
          errorMsg = "Network error during listening.";
        } else {
          errorMsg = `Listening error: ${event.error}`;
        }
        setError(errorMsg);
        // No auto-restart on deny; exit
        if (event.error !== "not-allowed") {
          setTimeout(startWakeListening, 2000);
        } else {
          setTimeout(exitVoiceMode, 2000);
        }
      };

      recognition.onend = () => {
        clearTimeout(timeoutsRef.current.wake);
        setIsListening(false);
        // No auto-restart here - relies on timeout or manual
      };

      recognition.start();
    } catch (err) {
      console.error("Failed to start wake listening:", err);
      setError("Failed to start listening. Check permissions.");
      exitVoiceMode();
    }
  }, [inVoiceMode, permissionDenied, clearStatus, exitVoiceMode, stopAllRecognitions, startQuestionRecording]);

  useEffect(() => {
    // Delay bobbing start to avoid load flicker
    const timer = setTimeout(() => {
      // BOBBING IS IN JSX, NO NEED
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    return () => {
      stopAllRecognitions();
      if ("speechSynthesis" in window) {
        window.speechSynthesis.cancel();
      }
    };
  }, [stopAllRecognitions]);

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 flex flex-col items-center justify-center p-4">
      <motion.div
        animate={{ y: [0, -8, 0] }}
        transition={{ repeat: Infinity, duration: 2 }}
        className="flex flex-col text-center justify-center items-center"
      >
        <RobotAvatar isSpeaking={isSpeaking} isRecording={isRecording} isListening={isListening} />
        <h1 className="text-2xl font-bold mb-2">Hi! I’m Thinky</h1>
        <h2 className="felx text-sm text-pink-300 mb-2">I bring Artificial MSME Intelligence for MSMEs, Banks, Government, Advisors, Consultants by blending AI and THink5Whys Database.</h2>
        <p className="text-sm text-gray-400 text-center">
          Ask me anything about <span className="text-pink-400">MSMEs</span>
        </p>
        <p className="h-5 text-sm mt-2 text-center min-h-[1.25rem]">
          {(status || error) && (
            <span className={`${error ? "text-red-400" : "text-blue-400"}`}>
              {status || error}
            </span>
          )}
        </p>
      </motion.div>

      <div className="w-full mt-6 flex flex-col gap-3 justify-center items-center">
        <input
          ref={inputRef}
          type="text"
          placeholder="Type your question... (Or press button for voice mode)"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          style={{width: "100%"}}
          className="p-3 rounded-lg bg-gray-800 text-gray-200 focus:outline-none focus:ring-2 focus:ring-pink-500"
          disabled={isRecording || isSpeaking} // Disable during active voice to avoid conflicts
          aria-label="Question input"
        />

        <button
          onClick={handleAsk}
          disabled={isRecording || isSpeaking || isListening}
          className="bg-pink-600 hover:bg-pink-500 text-white font-semibold py-3 rounded-lg shadow-md active:scale-95 transition disabled:opacity-50 w-full"
          aria-live="polite"
        >
          {isSpeaking ? "🎙️ Speaking..." : isListening ? "🎤 Listening for 'Thinky'..." : isRecording ? "🔴 Recording..." : "Ask Thinky"}
        </button>

        {answer && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-4 p-4 bg-gray-800 rounded-xl shadow-inner text-sm text-gray-200 w-full"
          >
            <p className="font-semibold text-pink-400 mb-1">Thinky says:</p>
            <p>{answer}</p>
          </motion.div>
        )}
      </div>
      <footer className="sticky bg-black border-t border-blue-400 text-white py-2 px-4 mt-2">
        <div className="flex flex-col md:flex-row justify-between items-center text-center md:text-left space-y-4 md:space-y-0">
          <p className="text-xs sm:text-sm">&copy; 2025 Think5Whys Global DeepTech Solutions.</p>
          <div className="flex flex-wrap justify-center md:justify-start gap-2 sm:gap-6 text-xs sm:text-sm">
            <Link to="/about-us" className="text-white hover:text-purple-200 transition">About</Link>
            <Link to="/privacy-policy" className="text-white hover:text-purple-200 transition">Privacy Policy</Link>
            <Link to="/terms-of-use" className="text-white hover:text-purple-200 transition">Terms of Use</Link>
            <Link to="/contact-us" className="text-white hover:text-purple-200 transition">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AMIHome;